import { forwardRef, ReactElement, ReactNode, memo } from 'react';
import cn from 'classnames';
import { IconType } from '@components/ui/Icons/iconTypes';
import styles from './Button.module.scss';
import IconWrapper from '../Icons';

export interface ButtonProps {
  type?: 'button' | 'submit';
  href?: string;
  label: string | ReactNode;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'unstyled';
  size: 'large' | 'small';
  className?: string;
  disabled?: boolean;
  handleClick?: (
    event: React.MouseEvent<HTMLElement | HTMLAnchorElement>
  ) => void;
  icon?: IconType;
  iconWidth?: number;
  iconHeight?: number;
  dataTestid?: string;
  ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
  target?: string;
  rel?: string;
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      type = 'button',
      href,
      label,
      variant = 'primary',
      size = 'large',
      className,
      disabled,
      handleClick,
      icon,
      iconWidth = 14,
      iconHeight = 13,
      dataTestid,
      target,
      rel,
    }: ButtonProps,
    ref
  ): ReactElement => {
    const rootClassName = cn(className, styles[variant], styles[size], {
      [styles.containerBase]: icon,
      [styles.disabled]: disabled,
    });
    const Component = href ? 'a' : 'button';
    const buttonType = Component === 'button' ? type : undefined;
    const hrefPath = href || undefined;

    const iconFill =
      variant === 'secondary' ? 'var(--color-black)' : 'var(--color-white)';
    return (
      <Component
        type={buttonType}
        href={hrefPath}
        target={target || undefined}
        rel={rel || undefined}
        className={rootClassName}
        onClick={handleClick}
        disabled={disabled}
        data-testid={dataTestid}
        ref={ref as React.Ref<HTMLButtonElement & HTMLAnchorElement>}
      >
        {icon && (
          <IconWrapper
            icon={icon}
            classNames={styles.iconButton}
            fill={iconFill}
            width={iconWidth}
            height={iconHeight}
          />
        )}
        {label}
      </Component>
    );
  }
);

export default memo(Button);
